import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Heart, Rocket, Globe, Users, HeartPulse, GraduationCap, Sparkles, MapPin } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const values = [
  { icon: Heart, title: "Mission that matters", desc: "Everything we build helps keep a real family safer. Your work is felt, not just shipped." },
  { icon: Rocket, title: "Move fast, build well", desc: "Small team, big ownership. Ship meaningful work without the layers of a giant org." },
  { icon: Globe, title: "Remote-friendly", desc: "Work from where you do your best thinking, with a hub in San Francisco for those who want it." },
  { icon: Sparkles, title: "Craft-obsessed", desc: "We sweat the details — in hardware, software, and the way we treat each other." },
];

const perks = [
  { icon: HeartPulse, label: "Full health, dental & vision" },
  { icon: GraduationCap, label: "Learning & growth budget" },
  { icon: Users, label: "Equity for every employee" },
  { icon: Globe, label: "Flexible, remote-first work" },
];

const roles = [
  { title: "Senior Embedded Systems Engineer", team: "Hardware", location: "San Francisco / Remote", type: "Full-time" },
  { title: "iOS Engineer (Swift)", team: "Mobile", location: "Remote (US)", type: "Full-time" },
  { title: "Computer Vision Engineer", team: "AI / On-device", location: "San Francisco", type: "Full-time" },
  { title: "Product Designer", team: "Design", location: "Remote (US)", type: "Full-time" },
  { title: "Customer Experience Lead", team: "Operations", location: "San Francisco / Remote", type: "Full-time" },
  { title: "Growth Marketing Manager", team: "Marketing", location: "Remote (US)", type: "Full-time" },
];

export default function Careers() {
  const navigate = useNavigate();
  const apply = (role) => {
    window.location.href = `mailto:careers@vsense.com?subject=${encodeURIComponent(`Application: ${role}`)}`;
  };

  return (
    <div data-testid="careers-page" className="bg-white">
      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-16 md:pb-24 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Careers at Vsense</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-zinc-900 leading-[1.05]">
              Build technology that watches over the people we love.
            </h1>
            <p className="mt-6 text-base md:text-lg text-zinc-500 max-w-2xl mx-auto leading-relaxed">
              We're a small, driven team designing safety glasses and software that give families a calmer,
              more connected way to look out for each other. Come help us build it.
            </p>
            <button
              data-testid="careers-hero-cta"
              onClick={() => document.getElementById("open-roles")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-9 inline-flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-800 active:scale-[0.98] transition-all duration-200"
            >
              See open roles <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* Image band */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-10 md:-mt-16">
          <Reveal>
            <div className="rounded-[2rem] overflow-hidden aspect-[16/9] shadow-[0_30px_80px_rgb(0,0,0,0.10)]">
              <img src="/products/about-hero.jpg" alt="The people behind Vsense" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900">
              Why you'll love it here.
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.05}>
                <div data-testid={`careers-value-${i}`}>
                  <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center">
                    <v.icon className="w-5 h-5 text-zinc-900" />
                  </div>
                  <h3 className="mt-4 font-semibold text-zinc-900">{v.title}</h3>
                  <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="bg-zinc-950 text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter">Benefits & perks</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {perks.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.05}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm md:text-base text-white/80 leading-snug">{p.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section id="open-roles" className="py-24 md:py-32 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900">
              Open roles
            </h2>
            <p className="mt-4 text-base md:text-lg text-zinc-500">
              Don't see the perfect fit? We're always glad to meet great people — email{" "}
              <a href="mailto:careers@vsense.com" className="font-medium text-zinc-900 underline-offset-4 hover:underline">careers@vsense.com</a>.
            </p>
          </Reveal>

          <div className="mt-12 divide-y divide-zinc-100 border-y border-zinc-100">
            {roles.map((r, i) => (
              <Reveal key={r.title} delay={(i % 3) * 0.04}>
                <div
                  data-testid={`career-role-${i}`}
                  className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-6"
                >
                  <div>
                    <h3 className="font-display text-lg md:text-xl font-bold tracking-tight text-zinc-900">{r.title}</h3>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-500">
                      <span>{r.team}</span>
                      <span className="hidden sm:inline text-zinc-300">·</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {r.location}</span>
                      <span className="hidden sm:inline text-zinc-300">·</span>
                      <span>{r.type}</span>
                    </div>
                  </div>
                  <button
                    data-testid={`career-apply-${i}`}
                    onClick={() => apply(r.title)}
                    className="shrink-0 inline-flex items-center gap-2 border border-zinc-300 text-zinc-900 text-sm font-medium px-6 py-2.5 rounded-full hover:border-zinc-900 hover:bg-zinc-900 hover:text-white active:scale-[0.98] transition-all duration-200"
                  >
                    Apply <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F5F5F7] py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900">
              Let's build it together.
            </h2>
            <p className="mt-5 text-base md:text-lg text-zinc-500">
              Have questions before applying? We'd love to hear from you.
            </p>
            <button
              data-testid="careers-contact-cta"
              onClick={() => navigate("/contact")}
              className="mt-9 inline-flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-800 active:scale-[0.98] transition-all duration-200"
            >
              Contact us <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
